import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import styled from "styled-components";
import { Box, Typography, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";

const OverlayContainer = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== "paymentstatus" && prop !== "show",
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: ${(props) =>
    props.paymentstatus === "overdue"
      ? "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)"
      : "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"};
  color: white;
  padding: 1rem 2rem;
  display: ${(props) => (props.show ? "flex" : "none")};
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${(props) => (props.show ? "slideDown 0.3s ease-out" : "none")};
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ContentWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const MessageWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StyledWarningIcon = styled(WarningIcon)`
  font-size: 2.5rem !important;
  animation: pulse 2s infinite;
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

const CloseButton = styled(IconButton)`
  color: white !important;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
  }
`;

const BlockingOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 2rem;
  text-align: center;
`;

const BlockingContent = styled(Box)`
  max-width: 600px;
  background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
`;

const PaymentDueOverlay = ({
  clientSlug = "arc-machine-tools-private-limited",
  apiKey = "WIjBDgFei3i8E6syIOWfickBYkDaxuY5",
  apiBaseUrl = "https://client-info.mushinaqua.com",
  pollInterval = 60000, // 60 seconds default
  onStatusChange,
}) => {
  const [clientStatus, setClientStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [forceRender, setForceRender] = useState(0); // Force re-render counter
  const dismissTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Constants for overlay identification
  const OVERLAY_ID = "payment-due-overlay";
  const BLOCKING_OVERLAY_ID = "payment-blocking-overlay";

  const fetchClientStatus = useCallback(async () => {
    if (!clientSlug || !apiKey) {
      console.warn("PaymentDueOverlay: clientSlug or apiKey not provided");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/public/clients/${clientSlug}`,
        {
          headers: {
            "x-api-key": apiKey,
          },
        }
      );

      const status = response.data;
      setClientStatus(status);

      // Block website if status is inactive
      const shouldBlock = status.status === "inactive" || !status.active;
      setIsBlocked(shouldBlock);

      // Show notification overlay if paymentStatus is overdue or warning
      const shouldShow =
        (status.paymentStatus === "overdue" ||
          status.paymentStatus === "warning") &&
        !isDismissed &&
        !shouldBlock;

      setShowOverlay(shouldShow);

      // Call callback if provided
      if (onStatusChange) {
        onStatusChange(status.status);
      }
    } catch (err) {
      console.error("Error fetching client status:", err);
      setError("Failed to fetch client status");
      // Don't show overlay on error to avoid blocking users
      setShowOverlay(false);
    } finally {
      setIsLoading(false);
    }
  }, [clientSlug, apiKey, apiBaseUrl, isDismissed, onStatusChange]);

  useEffect(() => {
    // Initial fetch
    fetchClientStatus();

    // Set up polling interval
    const intervalId = setInterval(fetchClientStatus, pollInterval);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchClientStatus, pollInterval]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setShowOverlay(false);

    // Clear any existing timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
    }

    // Reset dismissed state after pollInterval so overlay can reappear
    // if status is still inactive on next poll
    dismissTimeoutRef.current = setTimeout(() => {
      setIsDismissed(false);
      dismissTimeoutRef.current = null;
    }, pollInterval);
  };

  // Reset dismissed state when payment status changes
  useEffect(() => {
    if (
      clientStatus &&
      clientStatus.paymentStatus !== "overdue" &&
      clientStatus.paymentStatus !== "warning"
    ) {
      setIsDismissed(false);

      // Clear dismiss timeout if payment status is no longer overdue/warning
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = null;
      }
    }
  }, [clientStatus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Protection against DOM manipulation - detect overlay removal
  useEffect(() => {
    if (!showOverlay && !isBlocked) {
      // Clean up observers if overlay is not shown
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Check if overlay exists in DOM every 500ms
    const checkOverlayExists = () => {
      if (isBlocked) {
        // Check if blocking overlay still exists
        const blockingOverlay = document.getElementById(BLOCKING_OVERLAY_ID);
        if (!blockingOverlay) {
          console.warn("Blocking overlay was removed from DOM - re-rendering");
          setForceRender((prev) => prev + 1);
        }
      } else if (showOverlay) {
        // Check if notification overlay still exists
        const notificationOverlay = document.getElementById(OVERLAY_ID);
        if (!notificationOverlay) {
          console.warn(
            "Notification overlay was removed from DOM - re-rendering"
          );
          setForceRender((prev) => prev + 1);
        }
      }
    };

    // Set up periodic check
    checkIntervalRef.current = setInterval(checkOverlayExists, 500);

    // Set up MutationObserver to detect DOM changes
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check if the removed node is our overlay or contains it
            const isOverlay =
              node.id === OVERLAY_ID ||
              node.id === BLOCKING_OVERLAY_ID ||
              node.querySelector(`#${OVERLAY_ID}`) ||
              node.querySelector(`#${BLOCKING_OVERLAY_ID}`);

            if (isOverlay) {
              console.warn(
                "Overlay was removed via DOM manipulation - re-rendering"
              );
              setForceRender((prev) => prev + 1);
            }
          }
        });
      });
    });

    // Observe the document body for changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [showOverlay, isBlocked, forceRender]);

  // Prevent interaction with page when blocked
  useEffect(() => {
    if (isBlocked) {
      // Disable pointer events on body
      const originalStyle = document.body.style.pointerEvents;
      document.body.style.pointerEvents = "none";

      // Prevent right-click context menu
      const preventContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      // Prevent keyboard shortcuts (F12, Ctrl+Shift+I, etc.)
      const preventDevTools = (e) => {
        if (
          e.key === "F12" ||
          (e.ctrlKey && e.shiftKey && e.key === "I") ||
          (e.ctrlKey && e.shiftKey && e.key === "J") ||
          (e.ctrlKey && e.shiftKey && e.key === "C") ||
          (e.ctrlKey && e.key === "U")
        ) {
          e.preventDefault();
          return false;
        }
      };

      document.addEventListener("contextmenu", preventContextMenu);
      document.addEventListener("keydown", preventDevTools);

      return () => {
        document.body.style.pointerEvents = originalStyle;
        document.removeEventListener("contextmenu", preventContextMenu);
        document.removeEventListener("keydown", preventDevTools);
      };
    }
  }, [isBlocked]);

  // Get the message to display
  const getPaymentMessage = () => {
    if (!clientStatus) return "";

    if (clientStatus.paymentStatus === "overdue" && clientStatus.overdue) {
      return clientStatus.overdue.message;
    }

    if (clientStatus.paymentStatus === "warning" && clientStatus.warning) {
      return clientStatus.warning.message;
    }

    return "";
  };

  // Show blocking overlay if status is inactive
  if (isBlocked && clientStatus) {
    return (
      <BlockingOverlay
        id={BLOCKING_OVERLAY_ID}
        onContextMenu={(e) => e.preventDefault()}
        style={{ userSelect: "none" }}
      >
        <BlockingContent>
          <StyledWarningIcon
            style={{ fontSize: "4rem", marginBottom: "1rem" }}
          />
          <Typography
            variant="h4"
            style={{ fontWeight: 700, marginBottom: "1rem" }}
          >
            Account Inactive
          </Typography>
          <Typography
            variant="h6"
            style={{ marginBottom: "1rem", opacity: 0.9 }}
          >
            {"Mushin Aqua"} is currently inactive.
          </Typography>
          <Typography
            variant="body1"
            style={{ fontSize: "1.2rem", opacity: 0.8 }}
          >
            Please contact support or update your payment to continue using the
            service.
          </Typography>
        </BlockingContent>
      </BlockingOverlay>
    );
  }

  // Show notification overlay if payment is overdue or warning
  if (!showOverlay || isLoading) {
    return null;
  }

  const paymentMessage = getPaymentMessage();

  return (
    <OverlayContainer
      id={OVERLAY_ID}
      show={showOverlay}
      paymentstatus={clientStatus?.paymentStatus}
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: "none" }}
    >
      <ContentWrapper>
        <StyledWarningIcon />
        <MessageWrapper>
          <Typography
            variant="h6"
            style={{ fontWeight: 600, fontSize: "1.8rem" }}
          >
            Payment Due Notification
          </Typography>
          <Typography
            variant="body1"
            style={{ fontSize: "1.5rem", opacity: 0.95 }}
          >
            {paymentMessage ||
              "Please update your payment to continue using the service."}
          </Typography>
        </MessageWrapper>
      </ContentWrapper>
      <CloseButton onClick={handleDismiss} aria-label="Dismiss notification">
        <CloseIcon />
      </CloseButton>
    </OverlayContainer>
  );
};

export default PaymentDueOverlay;
